import { apiClient as api } from "./httpClient";

// Aplicação de avaliações ao paciente (F2 — RN-537). Permissão patient-assessments
// (Administrador + Profissional; 'own' = só as próprias aplicações). Rascunho com autosave
// por resposta; conclusão roda o motor e persiste resultados; reabertura em até 15 dias;
// exclusão de rascunho é lógica e apenas de rascunho.

export interface PatientAssessmentRow {
  id: string;
  formId: string;
  formTitle: string;
  formVersion: number;
  status: "in_progress" | "completed";
  professionalName: string | null;
  /** solicitacao = criacao do vinculo da avaliacao com o paciente */
  requestedAt: string;
  updatedAt: string | null;
  completedAt: string | null;
  answeredCount: number;
  questionsCount: number;
  /** prazo da janela de reabertura ("Editável até") — só para concluídas */
  reopenDeadline: string | null;
  /** Pode ABRIR o conteúdo (RN-540): autor, escopo de entidade ou acesso concedido. */
  canRead: boolean;
  canResume: boolean;
  canReopen: boolean;
  canDelete: boolean;
}

export interface AvailableFormRow {
  id: string;
  title: string;
  version: number;
  questionsCount: number;
}

export interface AssessmentFilterOption {
  id: string;
  name: string;
}

export interface PatientAssessmentsResult {
  items: PatientAssessmentRow[];
  availableForms: AvailableFormRow[];
  /** combos do filtro, montados antes dos filtros (nunca vêm vazios por causa do recorte) */
  formOptions: AssessmentFilterOption[];
  requesterOptions: AssessmentFilterOption[];
}

export interface PatientAssessmentFilters {
  formId?: string;
  search?: string;
  from?: string;
  to?: string;
  requestedBy?: string;
}

export interface FillOption {
  id: string;
  label: string;
  value: number | null;
}

export interface FillQuestion {
  id: string;
  sectionId: string | null;
  text: string;
  description: string | null;
  questionType: "single_choice" | "multiple_choice" | "short_text" | "long_text";
  required: boolean;
  allowComment: boolean;
  commentRequired: boolean;
  minChoices: number | null;
  maxChoices: number | null;
  ordenation: number;
  options: FillOption[];
}

export interface FillSection {
  id: string;
  parentSectionId: string | null;
  title: string;
  description: string | null;
  interpretationGuide: string | null;
  ordenation: number;
  hasCalc: boolean;
}

export interface FillAnswer {
  questionId: string;
  optionIds: string[];
  textValue: string | null;
  comment: string | null;
  /** trilha da resposta: quando e por quem foi gravada pela ultima vez */
  updatedAt: string;
  updatedByName: string | null;
}

export interface FillResultItem {
  label: string;
  value: number;
}

/** resultado persistido (só em concluída); value = número único (soma/média),
    breakdown = contagem por rótulo, uma linha por opção no banco */
export interface FillResult {
  sectionId: string | null;
  sectionTitle: string | null;
  calcTypeCode: string;
  calcTypeName: string;
  value: number | null;
  breakdown: FillResultItem[];
}

export interface AssessmentApplicationFill {
  id: string;
  patientId: string;
  patientName: string;
  formId: string;
  formTitle: string;
  formVersion: number;
  formDescription: string | null;
  interpretationGuide: string | null;
  allowPartialCompletion: boolean;
  /** all = tudo numa página; section = uma seção por vez */
  paginationMode: "all" | "section";
  status: "in_progress" | "completed";
  /** nulo na prévia (não há solicitação) */
  requestedAt: string | null;
  requestedByName: string | null;
  /** inicio do preenchimento = primeira resposta gravada */
  startedAt: string | null;
  startedByName: string | null;
  completedAt: string | null;
  completedByName: string | null;
  reopenDeadline: string | null;
  canEdit: boolean;
  canReopen: boolean;
  professionalInterpretation: string | null;
  sections: FillSection[];
  questions: FillQuestion[];
  answers: FillAnswer[];
  results: FillResult[];
  /** prévia do formulário montado: mesma tela, sem aplicação e sem gravar */
  isPreview: boolean;
  /** reaberta: em andamento, mas já teve uma conclusão */
  isReopened: boolean;
  /** resposta alterada depois do cálculo — o resultado gravado não vale mais */
  resultOutdated: boolean;
}

export interface AssessmentResultPreview {
  results: FillResult[];
  /** obrigatórias ainda sem resposta — a tela libera a conclusão por aqui */
  missingRequired: number;
}

export interface SaveAnswerPayload {
  optionIds: string[];
  textValue: string | null;
  comment: string | null;
}

export const PATIENT_ASSESSMENT_ERRORS: Record<string, string> = {
  "Assessment.ApplicationNotFound": "Aplicação não encontrada.",
  "Assessment.ApplicationNotDraft": "A aplicação não está em andamento.",
  "Assessment.ApplicationNotCompleted": "A aplicação não está concluída.",
  "Assessment.ReopenWindowExpired":
    "O prazo de reabertura (15 dias após a conclusão) expirou: a avaliação é definitiva.",
  "Assessment.NotYourApplication": "Esta aplicação pertence a outro profissional.",
  "Assessment.FormUnavailable": "Formulário inativo ou inexistente.",
  "Assessment.PatientNotFound": "Paciente não encontrado.",
  "Assessment.RequiredUnanswered":
    "Há perguntas obrigatórias sem resposta. Responda-as antes de concluir.",
  "Assessment.CommentRequiredMissing":
    "Há perguntas respondidas com comentário obrigatório em branco.",
  "Assessment.MinChoicesNotMet":
    "Há perguntas de múltipla escolha com menos opções marcadas que o mínimo exigido.",
  "Assessment.AnswerMismatch": "Resposta incompatível com a pergunta.",
  "Assessment.NotReopened": "A avaliação não está reaberta.",
  "Assessment.AnswersChangedSinceResult":
    "As respostas mudaram desde o último resultado: conclua novamente para atualizá-lo.",
  "Assessment.TextTooLong": "Texto excede o tamanho máximo.",
  "Assessment.Unauthorized": "Sessão sem entidade.",
};

export function patientAssessmentErrorMessage(error: unknown, fallback: string): string {
  const code = (error as { response?: { data?: { code?: string } } })?.response?.data?.code;
  if (code) {
    const known = PATIENT_ASSESSMENT_ERRORS[code];
    if (known) return known;
    const description = (error as { response?: { data?: { description?: string } } })
      ?.response?.data?.description;
    if (description) return description;
  }
  return fallback;
}

export const patientAssessmentsService = {
  async listByPatient(
    patientId: string,
    filters: PatientAssessmentFilters = {},
  ): Promise<PatientAssessmentsResult> {
    const { data } = await api.get(`/patient-assessments/by-patient/${patientId}`, {
      params: {
        formId: filters.formId || undefined,
        search: filters.search || undefined,
        from: filters.from || undefined,
        to: filters.to || undefined,
        requestedBy: filters.requestedBy || undefined,
      },
    });
    return data;
  },

  async getFill(applicationId: string): Promise<AssessmentApplicationFill> {
    const { data } = await api.get(`/patient-assessments/applications/${applicationId}`);
    return data;
  },

  /** Prévia do formulário montado (permissão do cadastro de formulários, não a do paciente).
      Funciona com formulário inativo — é o caso da versão recém-criada. */
  async getFormPreview(formId: string): Promise<AssessmentApplicationFill> {
    const { data } = await api.get(`/assessment-forms/${formId}/preview`);
    return data;
  },

  async start(formId: string, patientId: string): Promise<string> {
    const { data } = await api.post(`/patient-assessments/applications`, { formId, patientId });
    return data.id;
  },

  async saveAnswer(
    applicationId: string,
    questionId: string,
    payload: SaveAnswerPayload,
  ): Promise<void> {
    await api.put(
      `/patient-assessments/applications/${applicationId}/answers/${questionId}`,
      payload,
    );
  },

  /** Resultado com as respostas atuais, sem concluir (mesmo motor da conclusão). */
  async getResultPreview(applicationId: string): Promise<AssessmentResultPreview> {
    const { data } = await api.get(`/patient-assessments/applications/${applicationId}/result-preview`);
    return data;
  },

  async complete(applicationId: string, professionalInterpretation: string | null): Promise<void> {
    await api.post(`/patient-assessments/applications/${applicationId}/complete`, {
      professionalInterpretation,
    });
  },

  async reopen(applicationId: string): Promise<void> {
    await api.post(`/patient-assessments/applications/${applicationId}/reopen`);
  },

  /** Desfaz a reabertura (só quando nada mudou desde o resultado). */
  async cancelReopen(applicationId: string): Promise<void> {
    await api.post(`/patient-assessments/applications/${applicationId}/cancel-reopen`);
  },

  /** Planilha da avaliação: aba de respostas + aba de resultados (layout fixo). */
  async exportXlsx(applicationId: string, fileName: string): Promise<void> {
    const response = await api.get(
      `/patient-assessments/applications/${applicationId}/export`,
      { responseType: "blob" },
    );
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  /** exclusão LÓGICA de rascunho (com confirmação na tela) */
  async deleteDraft(applicationId: string): Promise<void> {
    await api.delete(`/patient-assessments/applications/${applicationId}`);
  },
};
